import { Box, Button, Center, Text, useDisclosure } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccount, useProvider, useSigner } from "wagmi";

import AmountSelector from "./AmountSelector";
import { ERROR_STREAM_EXISTS, hasDcaErrors } from "./DcaErrors";
import LengthSelector from "./LengthSelector";
import TokenSelector from "./TokenSelector";
import analyticsInstance from "../../config/analytics";
import { getSourceTokens, getTargetTokens } from "../../config/tokens";
import { getStream } from "../../helpers/streams";
import { ErrorModal } from "./ErrorModal";
import { isSupportedPair } from "../../config/resolver";
import { startDCA } from "../../helpers/dca";

function DcaConfig({ chainId, ...props }) {
  let {
    sourceToken,
    setSourceToken,
    targetToken,
    setTargetToken,
    length,
    setLength,
    amount,
    setAmount,
    dcaConfigErrors,
    setDcaConfigErrors,
  } = props;
  const { address } = useAccount();
  const signer = useSigner();
  const provider = useProvider();
  let navigate = useNavigate();

  const [dcaDisabled, setDcaDisabled] = useState(true);
  const [lfging, setLFGing] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // hook for resetting the source token when the target token changes
  useEffect(() => {
    setSourceToken("");
  }, [setSourceToken, targetToken]);

  // hook checking if it should enable/disable BTFDCA button
  useEffect(() => {
    if (!hasDcaErrors(dcaConfigErrors) && sourceToken && targetToken) setDcaDisabled(false);
    else setDcaDisabled(true);
  }, [dcaConfigErrors, sourceToken, targetToken]);

  // hook for checking if a stream already exists
  useEffect(() => {
    if (address && sourceToken && targetToken && isSupportedPair(chainId, sourceToken, targetToken)) {
      getStream(provider, chainId, address, sourceToken, targetToken).then((s) => {
        // sets an error if there's already a stream
        setDcaConfigErrors((prevErrs) => {
          let errs = { ...prevErrs };

          if (s.flowRate !== "0") errs[ERROR_STREAM_EXISTS] = ERROR_STREAM_EXISTS;
          else if (ERROR_STREAM_EXISTS in errs) delete errs[ERROR_STREAM_EXISTS];

          return errs;
        });
      });
    }
  }, [chainId, provider, address, sourceToken, targetToken, setDcaConfigErrors]);

  // BTFDCA button event handler
  const lfg = async () => {
    setLFGing(true);
    setDcaDisabled(true);

    analyticsInstance.track("LFGClicked", {
      sourceToken: sourceToken,
      targetToken: targetToken,
      length: length,
      amount: amount.toString(),
    });

    // (try to) create the superfluid stream
    startDCA(provider, chainId, signer.data, address, sourceToken, targetToken, length, amount)
      .then((r) => {
        if (r.hash) {
          analyticsInstance.track("LFGSuccess", {
            sourceToken: sourceToken,
            targetToken: targetToken,
            length: length,
            amount: amount.toString(),
          });
          console.log("success!");
          navigate("/success", { state: { txHash: r.hash } });
        } else {
          analyticsInstance.track("LFGFail", {
            sourceToken: sourceToken,
            targetToken: targetToken,
            length: length,
            amount: amount.toString(),
            response: r,
          });

          console.error("error dcaing!", r);

          // show error modal
          onOpen();
        }
      })
      .catch((e) => {
        analyticsInstance.track("LFGError", {
          sourceToken: sourceToken,
          targetToken: targetToken,
          length: length,
          amount: amount.toString(),
          error: e,
        });
        console.error("something's wrong!", e);

        // show error modal
        onOpen();
      })
      .finally(() => {
        setLFGing(false);
        setDcaDisabled(false);
      });
  };

  return (
    <>
      <Box bgColor="#35e130" textColor="black" p="2em" borderRadius="0.25em">
        <TokenSelector
          setSelectedOption={setTargetToken}
          selectedOption={targetToken}
          txtLabel="🛒 Buy"
          selectLabel="Asset to acquire"
          options={getTargetTokens(chainId)}
          tooltip="This is the asset we'll periodically acquire for you."
          mb="1em"
        />

        <TokenSelector
          setSelectedOption={setSourceToken}
          selectedOption={sourceToken}
          txtLabel="💳 With"
          selectLabel="Token to spend"
          options={getSourceTokens(chainId, targetToken)}
          tooltip="This is the token you're going to send to us."
          mb="1em"
        />

        <LengthSelector
          length={length}
          setLength={setLength}
          dcaConfigErrors={dcaConfigErrors}
          setDcaConfigErrors={setDcaConfigErrors}
          tooltip="You can keep buying forever (or until you run out of funds), or you can buy for a rolling period of time."
          mb="1em"
        />

        <AmountSelector
          chainId={chainId}
          sourceToken={sourceToken}
          targetToken={targetToken}
          amount={amount}
          setAmount={setAmount}
          length={length}
          dcaConfigErrors={dcaConfigErrors}
          setDcaConfigErrors={setDcaConfigErrors}
          tooltip="Are you feeling rich?"
          mb="1em"
        />

        <Center flexDirection="column">
          <Button
            id="lfgButton"
            size="lg"
            mt="1em"
            colorScheme="red"
            disabled={lfging || dcaDisabled}
            onClick={lfg}
            isLoading={lfging}
          >
            🚀 BTFDCA!
          </Button>
          <Text fontSize="xs" mt="1em">
            Invest safely. DYOR.
          </Text>
        </Center>
      </Box>
      <ErrorModal isOpen={isOpen} onClose={onClose} />
    </>
  );
}

export default DcaConfig;
